import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAtomValue } from 'jotai'
import { ChallengeService } from '@duck/api-definitions'
import { apis } from '@/modules/apiClients'
import { useAuth } from './useAuth'
import { useDebouncedEffect } from './useDebouncedEffect'
import { queryClient } from '@/modules/queryClient'
import { debugAccessGroupsAtom, getDebugAccessGroups } from '@/store/debugAtoms'

export const availableChallengesQueryKeys = {
  all: () => ['available-challenges'] as const,
  user: (isAuthenticated: boolean, accessGroupsOverride: string[]) =>
    ['available-challenges', isAuthenticated, accessGroupsOverride] as const,
}

const fetchAvailableChallenges =
  async (accessGroupsOverride: string[]): Promise<ChallengeService.ChallengesResponse> => {
    // Admin-only debug override (Konami panel); the backend ignores it for non-admins.
    const response = await apis.challengeService.getAvailableChallenges(
      accessGroupsOverride.length > 0 ? accessGroupsOverride.join(',') : undefined
    )
    return response.data
  }

const STALE_TIME = 30 * 60 * 1000 // 30 minutes

/** Warms the cache for the logged-in list so the challenges page renders
 *  instantly from it. Called from AuthInitializer as soon as a session exists.
 *  React Query dedupes against an in-flight page fetch, so a login that lands
 *  directly on the page still issues a single request. */
export const prefetchAvailableChallenges = () => {
  // Sorted so ['a','b'] and ['b','a'] share one cache entry/request.
  const accessGroupsOverride = [...getDebugAccessGroups()].sort()
  void queryClient.prefetchQuery({
    queryKey: availableChallengesQueryKeys.user(true, accessGroupsOverride),
    queryFn: () => fetchAvailableChallenges(accessGroupsOverride),
    staleTime: STALE_TIME,
  })
}

export const useAvailableChallenges = () => {
  const { isAuthenticated } = useAuth()
  const rawAccessGroupsOverride = useAtomValue(debugAccessGroupsAtom)
  // Sorted so ['a','b'] and ['b','a'] share one cache entry/request.
  const [accessGroupsOverride, setAccessGroupsOverride] = useState(() => [...rawAccessGroupsOverride].sort())

  // Debounced: the endpoint is rate-limited (20 requests/10min/IP), so rapid
  // MultiPick toggles must not each fire a request.
  useDebouncedEffect(() => {
    // Sorted so ['a','b'] and ['b','a'] share one cache entry/request.
    setAccessGroupsOverride([...rawAccessGroupsOverride].sort())
  }, [rawAccessGroupsOverride], 500)

  const query = useQuery({
    queryKey: availableChallengesQueryKeys.user(isAuthenticated, accessGroupsOverride),
    queryFn: () => fetchAvailableChallenges(accessGroupsOverride),
    staleTime: STALE_TIME,
    // Render whatever is cached (the login-time preload) immediately, but
    // always kick off a background refresh so changes since then show up.
    // Deduped against an in-flight fetch, so a cold visit fetches only once.
    refetchOnMount: 'always',
  })

  return {
    challenges: query.data?.availableChallenges ?? [],
    assignments: query.data?.availableAssignments ?? [],
    count: query.data?.count ?? 0,
    isLoading: query.isPending,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    clearAvailableChallenges: () => {
      queryClient.removeQueries({
        queryKey: availableChallengesQueryKeys.all(),
      })
    },
  }
}
